<script setup>
import DpIcon from './DpIcon';
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import {isEmpty} from 'lodash';
</script>

<script>
import DpExpand from "./DpExpand";
import DpReadValues from "./DpReadValues";
import DpTable from './DpTable';

export default {
  name: "DpSimpleCrud",
  components: {DpReadValues, DpExpand, DpTable},
  props: {
    modelValue: {type: [Object, Array]},
    keyMap: {type: Object},
    headline: {type: String},
    showDefaultSlot: {type: Boolean},
    readOnly: {type: Boolean, default: false},
    buttonLabel: {type: String, default: undefined},
    buttonIcon: {type: [Boolean, String], default: true},
    rtl: {type: Boolean, default: false},
    screenSizeIsMobile: {type: Boolean, default: false}
  },
  data() {
    return {
      isActive: this.slotValue,
      isDefaultSlotMounted: false
    };
  },
  computed: {
    slotValue() {
      return (this.readOnly) ? false : this.showDefaultSlot;
    }
  },
  watch: {
    showDefaultSlot: {
      handler() {
        if (this.slotValue === true)
          this.activateSlot();
        else
          this.deactivateSlot();
      }
    }
  },
  methods: {
    activateSlot() {
      this.isDefaultSlotMounted = true;
      this.$nextTick(() => {
        this.isActive = true;
        this.$emit('activate-slot');
      });
    },
    deactivateSlot() {
      this.isActive = false;
      setTimeout(() => {
        this.isDefaultSlotMounted = false;
        this.$emit('deactivate-slot');
      }, 300);
    }
  }
}
</script>

<template>
  <div class="dp-simple-crud">
    <div class="dp-simple-crud__head">
      <slot name="head">
        <div class="dp-flex justify-content__space-between"
             :class="{
                'row wrap align-items__center': !rtl && !screenSizeIsMobile,
                'row wrap align-items__center reverse': rtl && !screenSizeIsMobile,
                'column align-items__flex-end': rtl & screenSizeIsMobile,
                'column align-items__flex-start': !rtl & screenSizeIsMobile}">
          <h4>
            <slot name="headline">{{ headline }}</slot>
          </h4>
          <DpExpand>
            <dp-button v-show="!isActive && !readOnly" type="text" class="dp-simple-crud__head__action"
                       tabindex="0" @click="activateSlot" @keypress="activateSlot">
              <span><DpIcon v-if="buttonIcon" :icon="(buttonIcon === true) ? 'edit' : buttonIcon"/>{{ !isEmpty(buttonLabel) ? buttonLabel : t('edit.buttonLabel') }}</span>
            </dp-button>
          </DpExpand>
        </div>
      </slot>
    </div>
    <div class="dp-simple-crud__body">
      <div v-if="isDefaultSlotMounted">
        <DpExpand>
          <div v-show="isActive && !readOnly">
            <slot></slot>
          </div>
        </DpExpand>
      </div>
      <DpExpand>
        <div v-show="!isActive">
          <slot v-if="modelValue !== undefined" name="read" v-bind:data="modelValue">
            <DpTable v-if="Array.isArray(modelValue)" size="large" :data="modelValue" :columns="keyMap"></DpTable>
            <DpReadValues v-else :data="modelValue" :keyMap="keyMap" :screenSizeIsMobile="screenSizeIsMobile"></DpReadValues>
          </slot>
          <slot v-else name="read"></slot>
        </div>
      </DpExpand>
    </div>
  </div>
</template>

<style lang="scss">
@import '../scss_legacy/dp-color';

.dp-simple-crud {
  -webkit-border-radius: 8px;
  -moz-border-radius: 8px;
  border-radius: 8px;
  margin: 1rem 0;
  border: 2px solid nth($dp-ui-gradient, 2);

  .dp-simple-crud__head {
    padding: .75rem 1.5rem;
    border-bottom: 2px solid nth($dp-ui-gradient, 2);
  }

  .dp-simple-crud__body {
    padding: .75rem 1.5rem;
  }
}
</style>

<i18n>
{
  "ar-AE": {
    "edit": {
      "buttonLabel": "تحرير"
    }
  },
  "ar-EG": {
    "edit": {}
  },
  "ar-SA": {
    "edit": {}
  },
  "de-DE": {
    "edit": {
      "buttonLabel": "Bearbeiten"
    }
  },
  "de-AT": {
    "edit": {
      "buttonLabel": "Bearbeiten"
    }
  },
  "de-CH": {
    "edit": {
      "buttonLabel": "Bearbeiten"
    }
  },
  "en-US": {
    "edit": {
      "buttonLabel": "Edit"
    }
  },
  "en-GB": {
    "edit": {
      "buttonLabel": "Edit"
    }
  },
  "it-IT": {
    "edit": {
      "buttonLabel": "Modifica"
    }
  },
  "fr-FR": {
    "edit": {
      "buttonLabel": "Modifier"
    }
  },
  "es-ES": {
    "edit": {
      "buttonLabel": "Editar"
    }
  },
  "pl-PL": {
    "edit": {
      "buttonLabel": "Edytuj"
    }
  },
  "nl-NL": {
    "edit": {
      "buttonLabel": "Bewerken"
    }
  },
  "bg-BG": {
    "edit": {
      "buttonLabel": "Редактиране"
    }
  },
  "bs-BA": {
    "edit": {
      "buttonLabel": "Uredi"
    }
  },
  "da-DK": {
    "edit": {
      "buttonLabel": "Rediger"
    }
  },
  "et-EE": {
    "edit": {
      "buttonLabel": "Redigeeri"
    }
  },
  "fi-FI": {
    "edit": {
      "buttonLabel": "Muuta"
    }
  },
  "el-GR": {
    "edit": {
      "buttonLabel": "Επεξεργασία"
    }
  },
  "he-IL": {
    "edit": {
      "buttonLabel": "ערוך"
    }
  },
  "ga-IE": {
    "edit": {
      "buttonLabel": "Cuir in eagar"
    }
  },
  "ja-JP": {
    "edit": {
      "buttonLabel": "編集"
    }
  },
  "hr-HR": {
    "edit": {
      "buttonLabel": "Urediti"
    }
  },
  "lv-LV": {
    "edit": {
      "buttonLabel": "Rediģēt"
    }
  },
  "lt-LT": {
    "edit": {
      "buttonLabel": "Redaguoti"
    }
  },
  "pt-PT": {
    "edit": {
      "buttonLabel": "Editar"
    }
  },
  "ro-RO": {
    "edit": {
      "buttonLabel": "Editare"
    }
  },
  "ru-RU": {
    "edit": {
      "buttonLabel": "Редактировать"
    }
  },
  "sv-SE": {
    "edit": {
      "buttonLabel": "Redigera"
    }
  },
  "sk-SK": {
    "edit": {
      "buttonLabel": "Upraviť"
    }
  },
  "cs-CZ": {
    "edit": {
      "buttonLabel": "Upravit"
    }
  },
  "tr-TR": {
    "edit": {
      "buttonLabel": "Düzenle"
    }
  },
  "hu-HU": {
    "edit": {
      "buttonLabel": "Szerkeszt"
    }
  },
  "fr-BE": {
    "edit": {
      "buttonLabel": "Modifier"
    }
  },
  "fr-LU": {
    "edit": {
      "buttonLabel": "Modifier"
    }
  },
  "mt-MT": {
    "edit": {
      "buttonLabel": "Editja"
    }
  },
  "sl-SI": {
    "edit": {
      "buttonLabel": "Uredi"
    }
  },
  "el-CY": {
    "edit": {
      "buttonLabel": "Επεξεργασία"
    }
  },
  "tr-CY": {
    "edit": {
      "buttonLabel": "Düzenle"
    }
  },
  "uk-UA": {
    "edit": {}
  },
  "zh-CN": {
    "edit": {
      "buttonLabel": "编辑"
    }
  }
}
</i18n>
